import {
  Rendition, Publication, SpreadBlock, GlobalDirection, PageSpread, IndexedSpreadBlock,
} from '../types/epub';
import { partitionIndex } from '../../../../common/src/ts/utils';

export const buildSpreadBlock = (
  rendition: Readonly<Rendition>,
  linearIndex: number,
): SpreadBlock => {
  const primary = rendition.linearItems[linearIndex];
  if (primary === undefined) {
    throw new Error('Out of range.');
  }

  const rtl = rendition.direction === GlobalDirection.RTL;
  switch (primary.pageSpread) {
    case PageSpread.Center:
      return { center: primary };
    case PageSpread.Left: {
      const right = rendition.linearItems[rtl ? linearIndex - 1 : linearIndex + 1];
      if (right !== undefined && right.pageSpread === PageSpread.Right) {
        return { right, left: primary };
      }
      return { left: primary };
    }
    case PageSpread.Right: {
      const left = rendition.linearItems[rtl ? linearIndex + 1 : linearIndex - 1];
      if (left !== undefined && left.pageSpread === PageSpread.Left) {
        return { left, right: primary };
      }
      return { right: primary };
    }
  }
};

export const toOPFBasedPath = (
  publication: Readonly<Publication>,
  url: Readonly<string>,
): string => {
  if (url.indexOf(publication.resourceDir) === 0) {
    return url.substr(publication.resourceDir.length + 1);
  }
  return url;
};

export const supportsReflowableRendition = (publication: Readonly<Publication>): boolean => {
  return publication.renditions.length === 2;
};

export const getDefaultRendition = (publication: Readonly<Publication>): Rendition => {
  if (publication.renditions.length < 1) {
    throw new Error('Invalid argument.');
  }
  return publication.renditions[0];
};

export const getReflowableRendition = (publication: Readonly<Publication>): Rendition => {
  if (!supportsReflowableRendition(publication)) {
    throw new Error('This publication does not support reflowable rendition.');
  }
  return publication.renditions[1];
};

export const getPriorPageInBlock = (rendition: Rendition, targetPage: number): number => {
  if (targetPage === 0) {
    return 0;
  }

  const rtl = rendition.direction === GlobalDirection.RTL;
  const currentSpread = rendition.linearItems[targetPage].pageSpread;
  const prevSpread = rendition.linearItems[targetPage - 1].pageSpread;

  if (rtl) {
    if (currentSpread === PageSpread.Left && prevSpread === PageSpread.Right) {
      return targetPage - 1;
    }
  } else {
    if (currentSpread === PageSpread.Right && prevSpread === PageSpread.Left) {
      return targetPage - 1;
    }
  }

  return targetPage;
};

export const nextSpreadBlockPage = (rendition: Rendition, currentPage: number): number | null => {
  if (currentPage >= rendition.linearItems.length - 1) {
    return null;
  }

  const rtl = rendition.direction === GlobalDirection.RTL;
  const currentSpread = rendition.linearItems[currentPage].pageSpread;
  const nextSpread = rendition.linearItems[currentPage + 1].pageSpread;
  const nextIsLast = currentPage === rendition.linearItems.length - 2;

  if (rtl) {
    if (currentSpread === PageSpread.Right && nextSpread === PageSpread.Left) {
      return nextIsLast ? null : currentPage + 2;
    }
  } else {
    if (currentSpread === PageSpread.Left && nextSpread === PageSpread.Right) {
      return nextIsLast ? null : currentPage + 2;
    }
  }
  return currentPage + 1;
};

export const prevSpreadBlockPage = (rendition: Rendition, currentPage: number): number | null => {
  if (currentPage < 1) {
    return null;
  }

  const rtl = rendition.direction === GlobalDirection.RTL;
  const currentSpread = rendition.linearItems[currentPage].pageSpread;
  const prevSpread = rendition.linearItems[currentPage - 1].pageSpread;
  const prevIsFirst = currentPage === 1;

  if (rtl) {
    if (currentSpread === PageSpread.Left && prevSpread === PageSpread.Right) {
      return prevIsFirst ? null : getPriorPageInBlock(rendition, currentPage - 2);
    }
  } else {
    if (currentSpread === PageSpread.Right && prevSpread === PageSpread.Left) {
      return prevIsFirst ? null : getPriorPageInBlock(rendition, currentPage - 2);
    }
  }
  return getPriorPageInBlock(rendition, currentPage - 1);
};

export const isLinearItemPath = (path: string, rendition: Readonly<Rendition>): boolean => {
  const target = rendition.itemIndices[path];
  return (target == null) ? false : target.linear;
};

export function buildPageBlocks(rendition: Readonly<Rendition>): ReadonlyArray<IndexedSpreadBlock> {

  const result: IndexedSpreadBlock[] = [];
  for (let index = 0; index < rendition.linearItems.length;) {
    result.push({ ...buildSpreadBlock(rendition, index), index });
    index = nextSpreadBlockPage(rendition, index) || rendition.linearItems.length;
  }
  return result;
}

export function getBlockIndex(blocks: ReadonlyArray<IndexedSpreadBlock>,
                              page: number,
                              groupSize: number) {
  const index = Math.max(0, partitionIndex(blocks, pageBlock => pageBlock.index <= page) - 1);
  return index - index % groupSize;
}
